import React, { useState } from 'react';
import './SharedComponents.css';

/**
 * Reusable Education History Manager
 * Handles dynamic array of education entries
 * Used in both Register and EditProfile
 */

/* Theme-aware table cell styles */
const tdStyle = { backgroundColor: 'var(--surface-color)', color: 'var(--text-color)', borderColor: 'var(--border-color)' };
const thStyle = { backgroundColor: 'var(--hover-background)', color: 'var(--text-color)', borderColor: 'var(--border-color)' };

const EducationHistory = ({
  educationHistory,
  setEducationHistory,
  isRequired = false,
  showValidation = false,
  errorMsg = '',
  setErrorMsg = () => {}
}) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [newEducation, setNewEducation] = useState({
    level: '',
    degree: '',
    institution: ''
  });

  const handleEducationChange = (e) => {
    const { name, value } = e.target;
    setNewEducation(prev => ({ ...prev, [name]: value }));
  };

  const handleAddEducation = () => {
    // Validation
    if (!newEducation.level) {
      setErrorMsg("❌ Please select education level");
      return;
    }
    if (!newEducation.degree) {
      setErrorMsg("❌ Please enter degree type (e.g., BS, MS, PHD)");
      return;
    }
    if (!newEducation.institution) {
      setErrorMsg("❌ Please enter institution name");
      return;
    }

    // Add or update
    const educationEntry = {
      level: newEducation.level,
      degree: newEducation.degree,
      institution: newEducation.institution
    };

    if (editingIndex !== null) {
      // Update existing
      const updated = [...educationHistory];
      updated[editingIndex] = educationEntry;
      setEducationHistory(updated);
      setEditingIndex(null);
    } else {
      // Add new
      setEducationHistory([...educationHistory, educationEntry]);
    }

    // Reset form
    setNewEducation({
      level: '',
      degree: '',
      institution: ''
    });
    setErrorMsg('');
  };

  const handleEditEducation = (index) => {
    setNewEducation(educationHistory[index]);
    setEditingIndex(index);
  };

  const handleDeleteEducation = (index) => {
    setEducationHistory(educationHistory.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
      setNewEducation({
        level: '',
        degree: '',
        institution: ''
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setNewEducation({
      level: '',
      degree: '',
      institution: ''
    });
  };

  return (
    <div className="mb-4">
      <h5 className="mt-4 mb-3 text-primary">
        📚 Education History {isRequired && <span className="text-danger">*</span>}
      </h5>

      {/* Add/Edit Education Form */}
      <div className="mb-3">
        <div className="row mb-3">
          <div className="col-md-4">
            <label className="form-label">Education Level <span className="text-danger">*</span></label>
            <select
              className="form-control"
              name="level"
              value={newEducation.level}
              onChange={handleEducationChange}
            >
              <option value="">Select Level</option>
              <option value="Under Graduation">Under Graduation</option>
              <option value="Graduation">Graduation</option>
              <option value="Post Graduation">Post Graduation</option>
              <option value="PHD">PHD</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Degree Type <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              name="degree"
              value={newEducation.degree}
              onChange={handleEducationChange}
              placeholder="e.g., BS, MS, PHD"
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">Institution Name <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              name="institution"
              value={newEducation.institution}
              onChange={handleEducationChange}
              placeholder="e.g., Georgia Tech, MIT, Stanford"
            />
          </div>

          <div className="col-md-2 d-flex align-items-end justify-content-center">
            <button
              type="button"
              className="btn btn-warning btn-square-action btn-edu-add-action"
              onClick={handleAddEducation}
            >
              <span className="btn-edu-add-label">{editingIndex !== null ? 'Update Education' : 'Add Education'}</span>
            </button>
          </div>
        </div>

        {editingIndex !== null && (
          <div className="mt-2">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleCancelEdit}
            >
              Cancel Edit
            </button>
          </div>
        )}
      </div>

      {/* Saved Education Entries */}
      {educationHistory.length > 0 && (
        <div className="mb-3 table-container-scroll">
          <table className="table table-bordered table-stacked" style={{ backgroundColor: 'var(--surface-color, #fff)', borderColor: 'var(--border-color, #dee2e6)' }}>
            <thead style={{ backgroundColor: 'var(--hover-background)', borderColor: 'var(--border-color)' }}>
              <tr>
                <th style={thStyle}>Level</th>
                <th style={thStyle}>Degree</th>
                <th style={thStyle}>Institution</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {educationHistory.map((edu, index) => (
                <tr key={index}>
                  <td data-label="Level" style={tdStyle}>{edu.level}</td>
                  <td data-label="Degree" style={tdStyle}>{edu.degree}</td>
                  <td data-label="Institution" style={tdStyle}>{edu.institution}</td>
                  <td data-label="Actions" style={tdStyle} className="actions-cell">
                    <button
                      type="button"
                      className="btn btn-sm btn-warning me-2"
                      onClick={() => handleEditEducation(index)}
                      title="Edit"
                    >
                      <span className="btn-icon">✏️</span>
                      <span className="btn-text">Edit</span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteEducation(index)}
                      title="Delete"
                    >
                      <span className="btn-icon">🗑️</span>
                      <span className="btn-text">Delete</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showValidation && isRequired && educationHistory.length === 0 && (
        <small className="text-danger d-block mb-2">
          ⚠️ Please add at least one education entry
        </small>
      )}
    </div>
  );
};

export default EducationHistory;
